import { integrationApi, type RuleNode } from "@repo/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const correlationRulesKey = (integrationId: number) =>
  ["correlationRules", integrationId] as const;

export function useCorrelationRules(integrationId: number | undefined) {
  return useQuery({
    queryKey: correlationRulesKey(integrationId ?? 0),
    queryFn: async () => {
      if (!integrationId) throw new Error("Integration ID required");
      return integrationApi.getCorrelationRules(integrationId);
    },
    enabled: !!integrationId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

export function useSaveCorrelationRule(integrationId: number | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rule: RuleNode) => {
      if (!integrationId) throw new Error("Integration ID required");
      return integrationApi.addCorrelationRules(integrationId, [rule]);
    },
    onSuccess: () => {
      if (!integrationId) return;
      void queryClient.invalidateQueries({
        queryKey: correlationRulesKey(integrationId),
      });
    },
  });
}

export function useDeleteCorrelationRule(integrationId: number | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ruleId: number) => {
      if (!integrationId) throw new Error("Integration ID required");
      return integrationApi.deleteCorrelationRule(integrationId, ruleId);
    },
    onSuccess: () => {
      if (!integrationId) return;
      queryClient.invalidateQueries({
        queryKey: correlationRulesKey(integrationId),
      });
    },
  });
}
